
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { CalendarEvent } from "@/types/calendar";
import { useViewAs } from "@/contexts/ViewAsContext";
import { mockCalendarEvents } from "@/utils/mockCalendarData";
import { toast } from "sonner";

export const useEvents = (start: Date, end: Date) => {
  const { viewAsUserId } = useViewAs();
  
  // Get the current user ID if viewAsUserId is not set
  const getUserId = async () => {
    if (viewAsUserId) return viewAsUserId;
    
    const { data } = await supabase.auth.getUser();
    return data.user?.id;
  };

  return useQuery({
    queryKey: ['calendar-events', format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'), viewAsUserId],
    queryFn: async () => {
      try {
        console.log(`Fetching events from ${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`);
        const userId = await getUserId();
        
        // For demo purposes, always include some mock events
        // We filter mock events to only include those within the date range
        const filteredMockEvents = mockCalendarEvents.filter(event => {
          const eventStart = new Date(event.start_time);
          return eventStart >= start && eventStart <= end;
        });
        
        if (!userId) {
          console.log("No user ID, returning filtered mock events");
          return filteredMockEvents;
        }

        const { data, error } = await supabase
          .from('calendar_events')
          .select('*')
          .eq('user_id', userId)
          .gte('start_time', start.toISOString())
          .lte('start_time', end.toISOString())
          .order('start_time', { ascending: true });

        if (error) throw error;
        
        // Combine database events with mock events
        const dbEvents = data || [];
        const allEvents = [...dbEvents, ...filteredMockEvents];
        
        console.log(`Found ${dbEvents.length} database events and ${filteredMockEvents.length} mock events`);
        return allEvents as CalendarEvent[];
        
      } catch (error) {
        console.error('Error fetching events:', error);
        return mockCalendarEvents.filter(event => {
          const eventStart = new Date(event.start_time);
          return eventStart >= start && eventStart <= end;
        });
      }
    },
  });
};

export const useAddEvent = () => {
  const queryClient = useQueryClient();
  const { viewAsUserId } = useViewAs();
  
  // Get the current user ID if viewAsUserId is not set
  const getUserId = async () => {
    if (viewAsUserId) return viewAsUserId;
    
    const { data } = await supabase.auth.getUser();
    return data.user?.id;
  };
  
  return useMutation({
    mutationFn: async (newEvent: Omit<CalendarEvent, 'id' | 'user_id'>) => {
      const userId = await getUserId();
      
      if (!userId) throw new Error("User ID not available");
      
      const eventToInsert = {
        ...newEvent,
        user_id: userId,
        source: 'manual' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('calendar_events')
        .insert([eventToInsert])
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: (newEvent) => {
      // Invalidate all calendar event queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      
      // Also update the cache optimistically
      queryClient.setQueryData(['calendar-events'], (old: any) => {
        if (!old) return [newEvent];
        return [...old, newEvent];
      });
      
      console.log('Event created successfully:', newEvent);
      toast.success("Event created successfully!");
    },
    onError: (error) => {
      console.error('Error creating event:', error);
      toast.error("Failed to create event. Please try again.");
    }
  });
};

export const useUpdateEvent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (event: CalendarEvent) => {
      const { id, ...updates } = event;
      const { data, error } = await supabase
        .from('calendar_events')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success("Event updated successfully!");
    },
    onError: (error) => {
      console.error('Error updating event:', error);
      toast.error("Failed to update event. Please try again.");
    }
  });
};

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      toast.success("Event deleted successfully!");
    },
    onError: (error) => {
      console.error('Error deleting event:', error);
      toast.error("Failed to delete event. Please try again.");
    }
  });
};
